import {
  Box,
  Button,
  Flex,
  Image,
  keyframes,
  Spacer,
  Text,
} from '@chakra-ui/react';
import Editor from '@monaco-editor/react';
import React, { useMemo, useState } from 'react';
import { format } from 'sql-formatter';

import { colors } from 'packages/common-frontend/colors';
import squareLogo from 'packages/common-frontend/images/Logo-square.svg';
import { getEditorHeight } from 'packages/finleyAi/ui/FinleyAiUtils';

export const FinleyAiUserBadge = ({
  label,
  type,
}: {
  label: string;
  type: 'user' | 'finleyAi';
}) => {
  return (
    <Box
      borderRadius="8px"
      backgroundColor={
        type === 'finleyAi' ? colors.secondary[100] : colors.black[20]
      }
      p="4px 8px"
      w="fit-content"
      cursor="default"
    >
      {label}
    </Box>
  );
};

const sqlFormat: (queryString: string) => string = (queryString: string) =>
  format(queryString, {
    language: 'trino', // athena uses trino & presto
  });

export type OnClickSqlAction = {
  callback: (sql: string) => void;
  label: string;
};

export const FinleyAiPromptResponseSqlDisplay = ({
  sqlString,
  onClickSqlAction,
}: {
  sqlString: string;
  onClickSqlAction: OnClickSqlAction;
}) => {
  const formattedSql = sqlFormat(sqlString);

  const editorHeight = useMemo(() => getEditorHeight(formattedSql), [
    formattedSql,
  ]);

  return (
    <Box
      backgroundColor={colors.background[50]}
      p="16px"
      borderRadius="8px"
      borderWidth="1px"
      borderColor={colors.gray[200]}
    >
      <Flex alignItems="center" justifyContent="space-between">
        <Text fontWeight={600} color={colors.black[95]}>
          SQL
        </Text>
        <Button
          colorScheme="aurora"
          variant="outline"
          borderWidth="2px"
          backgroundColor="white"
          color={colors.aurora[400]}
          onClick={() => onClickSqlAction.callback(formattedSql)}
        >
          {onClickSqlAction.label}
        </Button>
      </Flex>
      <Box
        mt="16px"
        minH="120px"
        maxH="400px"
        h={editorHeight}
        width="100%"
        overflowY="visible"
        py="16px"
        backgroundColor="white"
        borderRadius="8px"
        borderWidth="1px"
        borderColor={colors.gray[200]}
      >
        <Editor
          value={formattedSql}
          language="sql"
          theme="clouds"
          options={{
            readOnly: true,
            lineNumbers: 'off',
            overviewRulerBorder: false,
            overviewRulerLanes: 0,
            minimap: { enabled: false },
            renderLineHighlight: 'none',
            scrollbar: { vertical: 'hidden' },
          }}
        />
      </Box>
    </Box>
  );
};

export const PromptSuggestionButton = ({
  label,
  onClick,
}: {
  label: string;
  onClick: () => void;
}) => {
  return (
    <Button
      onClick={onClick}
      mt="16px"
      p="4px 8px"
      borderRadius="4px"
      h="28px"
      fontSize="14px"
    >
      {label}
    </Button>
  );
};

export const PromptChatBubble = ({
  promptString,
}: {
  promptString: string;
}) => {
  return (
    <Flex alignItems="flex-end" w="100%" mt="16px">
      <Spacer />
      <Text
        textAlign="right"
        whiteSpace="pre-line"
        borderRadius="32px"
        backgroundColor={colors.black[10]}
        p="8px 16px"
        color={colors.black[95]}
      >
        {promptString}
      </Text>
    </Flex>
  );
};

const FinleyLogoSpinner = () => {
  const [isLogoLoaded, setIsLogoLoaded] = useState<boolean>(false);

  const spinWithPause = keyframes`
0% { transform: rotate(0deg); }
50% { transform: rotate(180deg); }
100% { transform: rotate(180deg); }
`;

  return (
    <Image
      src={squareLogo}
      alt="Logo"
      cursor="pointer"
      display={isLogoLoaded ? 'block' : 'none'}
      h="24px"
      w="fit-content"
      onLoad={() => {
        setIsLogoLoaded(true);
      }}
      animation={`${spinWithPause} 2s linear infinite`}
    />
  );
};

export const FinleyThinkingSpinner = () => {
  const ellipsis = keyframes`
0% { content: '...'; }
25% { content: ''; }
50% { content: '.'; }
75% { content: '..'; }
100% { content: '...'; }
`;
  return (
    <Flex alignItems="center">
      <FinleyLogoSpinner />
      <Text ml="8px" fontWeight={600}>
        Thinking
      </Text>
      <Text
        animation={`${ellipsis} 2s steps(4, end) infinite`}
        _after={{
          content: "''",
          animation: `${ellipsis} 2s steps(4, end) infinite`,
        }}
      />
    </Flex>
  );
};
